import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Download, BookOpen, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const MyEbooks = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Carrega compras do localStorage
    const allPurchases = JSON.parse(localStorage.getItem('purchases') || '[]');
    const userPurchases = allPurchases.filter(p => p.userId === user.id);
    setPurchases(userPurchases);
  }, [isAuthenticated, navigate, user]);

  const handleDownload = (ebook) => {
    // Mock: simula download
    toast.success(`Download de "${ebook.title}" iniciado!`);
    // Em produção, aqui faria o download real do PDF
    console.log('Downloading:', ebook.title);
  };

  const getPurchasedEbooks = () => {
    const allEbooks = [];
    purchases.forEach(purchase => {
      purchase.items.forEach(item => {
        if (!allEbooks.find(e => e.id === item.id)) {
          allEbooks.push(item);
        }
      });
    });
    return allEbooks;
  };

  const purchasedEbooks = getPurchasedEbooks();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh', paddingTop: '120px', paddingBottom: '64px' }}>
      <div className="container">
        <div style={{ marginBottom: '32px' }}>
          <h1 className="display-medium" style={{ marginBottom: '8px' }}>
            Meus E-books
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Bem-vindo, {user?.name}! Aqui estão seus programas de treinamento.
          </p>
        </div>

        {purchasedEbooks.length === 0 ? (
          <div className="network-card" style={{ 
            textAlign: 'center',
            padding: '64px 32px'
          }}>
            <BookOpen size={80} color="var(--text-light)" style={{ marginBottom: '24px' }} />
            <h2 className="heading-2" style={{ marginBottom: '16px' }}>
              Você ainda não possui e-books
            </h2>
            <p className="body-medium" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
              Explore nossa coleção e comece sua jornada de transformação hoje!
            </p>
            <Link to="/produtos">
              <button className="btn-primary">Ver Produtos</button>
            </Link>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '32px',
              marginBottom: '48px'
            }}>
              {purchasedEbooks.map(ebook => (
                <div key={ebook.id} className="network-card" style={{ padding: '0', overflow: 'hidden' }}>
                  <div style={{ position: 'relative' }}>
                    <img 
                      src={ebook.image} 
                      alt={ebook.title}
                      style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'var(--brand-primary)',
                      color: 'var(--brand-dark)',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontWeight: '700',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <CheckCircle size={14} />
                      Adquirido
                    </div>
                  </div>
                  
                  <div style={{ padding: '24px' }}>
                    <p className="caption" style={{ color: 'var(--brand-dark)', fontWeight: '600', marginBottom: '8px' }}>
                      {ebook.target}
                    </p>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                      {ebook.title}
                    </h3>
                    <p className="body-small" style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                      {ebook.subtitle}
                    </p>

                    <button 
                      className="btn-primary"
                      onClick={() => handleDownload(ebook)}
                      style={{ 
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Download size={18} />
                      Baixar E-book (PDF)
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Histórico de Compras */}
            <div className="network-card">
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Histórico de Compras
              </h2>
              {purchases.map(purchase => (
                <div key={purchase.id} style={{
                  padding: '20px',
                  background: 'var(--bg-subtle)',
                  borderRadius: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div>
                      <p className="body-medium" style={{ fontWeight: '600', marginBottom: '4px' }}>
                        Pedido #{purchase.id}
                      </p>
                      <p className="caption" style={{ color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        {new Date(purchase.date).toLocaleDateString('pt-BR', { 
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p className="caption" style={{ marginBottom: '4px' }}>Total</p>
                      <p className="heading-3" style={{ color: 'var(--brand-dark)' }}>
                        R$ {purchase.total.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div style={{ paddingTop: '12px', borderTop: '1px solid var(--border-light)' }}>
                    {purchase.items.map((item, idx) => (
                      <p key={idx} className="body-small" style={{ marginBottom: '4px' }}>
                        • {item.title}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyEbooks;